import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CardSystemEditor from "../elementEditors/CardSystemEditor";
import { getSegmentDefinition, SEGMENT_TYPES } from "../segmentRegistry";
import { getLandingPreviewDevice } from "../previewDevices";
import { isCanonicalGastronomicCatalogNavigation, resolveSiteExperienceDefaults } from "../../landingEditor/siteExperienceDefaults";
import {
  productsPageButtonSx,
  productsPageChipSx,
  productsPageHeaderBackgroundCss,
  productsPageInputSx,
  productsPageSurfaceSx,
  resolveProductsPageVisual,
} from "../productsPageVisualOwnership";
import {
  ProductsFiltersVisualEditor,
  ProductsHeaderCtaVisualEditor,
  ProductsHeaderPieceVisualEditor,
  ProductsPageGeneralVisualEditor,
  ProductsPaginationVisualEditor,
  ProductsSearchVisualEditor,
} from "./ProductsPageStyleEditors";
import {
  EditorGroup,
  NumberSetting,
  SegmentedChoiceSetting,
  PageEditorShell,
  PagePreviewTarget,
  PageHiddenTarget,
  PageEditorContextIntro,
  PageEditorBackButton,
  SelectSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";

const PREVIEW_PRODUCT_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="960" height="640" viewBox="0 0 960 640">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#e9ecf4"/>
        <stop offset="1" stop-color="#cfd6e6"/>
      </linearGradient>
    </defs>
    <rect width="960" height="640" rx="36" fill="url(#bg)"/>
    <circle cx="760" cy="130" r="92" fill="#ffffff" opacity=".65"/>
    <rect x="285" y="118" width="390" height="404" rx="54" fill="#ffffff"/>
    <rect x="352" y="172" width="256" height="214" rx="26" fill="#6a5ae0" opacity=".88"/>
    <rect x="374" y="414" width="212" height="34" rx="17" fill="#19242f" opacity=".78"/>
    <rect x="414" y="468" width="132" height="18" rx="9" fill="#19242f" opacity=".34"/>
  </svg>
`)}`;

function normalizedCardImageHeight(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 210;
  return Math.min(420, Math.max(90, parsed));
}

function previewCardImagePosition(position) {
  if (position === "left") return "left center";
  if (position === "right") return "right center";
  return "center top";
}

function pageValue(form) {
  return form?.catalog?.productsPage || {};
}

function cardValue(form) {
  return form?.catalog?.cards || {};
}


function numericInRange(value, fallback, min, max) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
}

function colorWithOpacity(color, opacity, fallback = "#FFFFFF") {
  const candidate = String(color || fallback || "#FFFFFF").trim() || fallback;
  const alpha = numericInRange(opacity, 1, 0, 1);
  if (alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;
  return `color-mix(in srgb, ${candidate} ${Math.round(alpha * 1000) / 10}%, transparent)`;
}

function previewCardShadow(cards) {
  const configured = ["shadowOpacity", "shadowX", "shadowY", "shadowBlur", "shadowSpread"]
    .some((key) => cards?.[key] !== undefined && cards?.[key] !== null && cards?.[key] !== "");
  if (!configured) return cards?.shadowColor ? `0 8px 24px ${cards.shadowColor}` : "0 10px 26px rgba(25,36,47,.10)";
  const opacity = numericInRange(cards?.shadowOpacity, 1, 0, 1);
  if (opacity <= 0) return "none";
  const x = numericInRange(cards?.shadowX, 0, -100, 100);
  const y = numericInRange(cards?.shadowY, 8, -100, 100);
  const blur = numericInRange(cards?.shadowBlur, 24, 0, 160);
  const spread = numericInRange(cards?.shadowSpread, 0, -80, 80);
  return `${x}px ${y}px ${blur}px ${spread}px ${colorWithOpacity(cards?.shadowColor || "#000000", opacity, "#000000")}`;
}

const TARGETS = Object.freeze({
  page: { label: "Página", hint: "Fondo, superficies y estados" },
  eyebrow: { label: "Texto superior", hint: "Copy del encabezado" },
  title: { label: "Título", hint: "Título general y variantes" },
  subtitle: { label: "Subtítulo", hint: "Descripción del catálogo" },
  results: { label: "Resultados", hint: "Singular y plural" },
  headerCta: { label: "CTA Ver carta", hint: "Apariencia local del acceso a la carta" },
  search: { label: "Buscador", hint: "Visibilidad y placeholder" },
  filters: { label: "Filtros", hint: "Visibilidad y etiquetas" },
  sort: { label: "Orden", hint: "Orden predeterminado" },
  pagination: { label: "Paginación", hint: "Modo y cantidad por página" },
  cards: { label: "Cards del catálogo", hint: "Sistema compartido" },
});

function targetMeta(targetId) {
  return TARGETS[targetId] || TARGETS.page;
}

const ProductsPreviewEditingContext = React.createContext(true);

function ProductsPreviewTarget({ targetId, activeTarget, onSelect, label, children, sx = {} }) {
  const editable = React.useContext(ProductsPreviewEditingContext);
  if (!editable) return <Box sx={sx}>{children}</Box>;
  return (
    <PagePreviewTarget
      targetId={targetId}
      activeTarget={activeTarget}
      onSelect={onSelect}
      label={label || targetMeta(targetId).label}
      sx={sx}
    >
      {children}
    </PagePreviewTarget>
  );
}

function ProductsHiddenTarget(props) {
  const editable = React.useContext(ProductsPreviewEditingContext);
  if (!editable) return null;
  return <PageHiddenTarget {...props} />;
}

function sortPreviewLabel(page) {
  const by = page.sortBy || "createdAt";
  const dir = page.sortDir || "desc";
  const byLabel = by === "price" ? "Precio" : by === "name" ? "Nombre" : "Más recientes";
  return `${byLabel} · ${dir === "asc" ? "Ascendente" : "Descendente"}`;
}

function productsPreviewTitle(page, navigation, catalogContext) {
  if (page.title) return page.title;
  if (catalogContext === "product") return page.productsTitle || "Productos";
  if (catalogContext === "service") return page.servicesTitle || "Servicios";
  if (catalogContext === "hybrid") return page.hybridTitle || "Productos + servicio";
  return navigation?.productsLabel || "Productos y servicios";
}

export function ProductsPagePreview({ form, activeTarget, onSelectTarget, catalogContext, previewState, device, editable = true, showHeaderCta = false }) {
  const page = pageValue(form);
  const cards = cardValue(form);
  const visual = resolveProductsPageVisual(page.visual);
  const previewDevice = getLandingPreviewDevice(device);
  const isDesktop = previewDevice.id === "desktop";
  const isMobile = previewDevice.id === "mobile";
  const filtersVisible = page.showFilters !== false;
  const compactFilters = filtersVisible && !isDesktop;
  const background = visual.page.backgroundColor;
  const cardBackground = colorWithOpacity(cards.backgroundColor || "#FFFFFF", cards.backgroundOpacity ?? 1, "#FFFFFF");
  const cardRadius = numericInRange(cards.radius, 16, 0, 48);
  const cardGap = numericInRange(cards.gap, 18, 0, 64);
  const cardShadow = previewCardShadow(cards);
  const cardBackgroundImage = String(cards.backgroundImageUrl || "").trim();
  const cardBackgroundImageOpacity = numericInRange(cards.backgroundImageOpacity, 0.28, 0, 1);
  const cardBackgroundImageFit = cards.backgroundImageFit === "contain" ? "contain" : "cover";
  const cardBackgroundImagePosition = ["center", "top", "bottom", "left", "right"].includes(cards.backgroundImagePosition)
    ? cards.backgroundImagePosition
    : "center";
  const cardImagePosition = ["top", "left", "right"].includes(cards.imagePosition) ? cards.imagePosition : "top";
  const cardImageSide = cardImagePosition === "left" || cardImagePosition === "right";
  const showCardImage = cards.showImage !== false;
  const cardImageHorizontal = showCardImage && cardImageSide && isDesktop;
  const cardImageHeight = normalizedCardImageHeight(cards.imageHeight);
  const collapsedImageHeight = Math.max(120, Math.round(cardImageHeight * 0.76));
  const previewImageHeight = cardImageSide
    ? (isDesktop ? cardImageHeight : collapsedImageHeight)
    : (isMobile ? collapsedImageHeight : cardImageHeight);
  const cardImageFit = cards.imageFit === "contain" ? "contain" : "cover";
  const cardImageRight = cardImagePosition === "right";
  const previewResultsText = previewState === "loading"
    ? (page.loadingText ?? "Cargando catálogo…")
    : previewState === "empty"
      ? `0 ${page.resultPlural ?? "resultados"}`
      : `12 ${page.resultPlural || "resultados"}`;
  const emptyTitle = page.emptyTitle ?? "No encontramos resultados";
  const emptyText = page.emptyText ?? "Probá con otra búsqueda o quitá los filtros activos.";
  const emptyResetLabel = page.emptyResetLabel ?? "Ver todo el catálogo";

  return (
    <ProductsPreviewEditingContext.Provider value={editable}>
    <Box
      sx={{
        minHeight: 680,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        bgcolor: background,
        color: visual.filters.title.color,
        position: "relative",
      }}
      onClick={editable ? () => onSelectTarget?.("page") : undefined}
    >
      {editable ? (
        <Button
          size="small"
          variant={activeTarget === "page" ? "contained" : "outlined"}
          startIcon={<TuneRoundedIcon />}
          onClick={(event) => {
            event.stopPropagation();
            onSelectTarget?.("page");
          }}
          sx={{ position: "absolute", zIndex: 8, top: 10, right: 10, bgcolor: activeTarget === "page" ? undefined : "rgba(255,255,255,.94)" }}
        >
          Página
        </Button>
      ) : null}

      <Box sx={{ px: isDesktop ? 5 : 2, pt: isDesktop ? 9 : 7, pb: 5, background: productsPageHeaderBackgroundCss(visual.header.background) }}>
        <Stack spacing={1.1} sx={{ minWidth: 0 }}>
          <ProductsPreviewTarget targetId="eyebrow" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" variant="overline" sx={{ color: visual.header.eyebrow.color, opacity: page.eyebrow ? 1 : 0.58, fontStyle: page.eyebrow ? "normal" : "italic" }}>
              {page.eyebrow || "Texto superior"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="title" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
            <Typography data-preview-passive="true" variant="h3" sx={{ color: visual.header.title.color, fontWeight: 900, overflowWrap: "anywhere" }}>
              {productsPreviewTitle(page, form?.navigation, catalogContext)}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="subtitle" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
            <Typography data-preview-passive="true" sx={{ color: visual.header.subtitle.color, opacity: page.subtitle ? 1 : 0.58, fontStyle: page.subtitle ? "normal" : "italic", overflowWrap: "anywhere" }}>
              {page.subtitle || "Subtítulo opcional"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="results" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" sx={{ color: visual.header.results.color }}>
              {previewResultsText}
            </Typography>
          </ProductsPreviewTarget>
          {showHeaderCta ? (
            <ProductsPreviewTarget targetId="headerCta" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ alignSelf: "flex-start" }}>
              <Button tabIndex={-1} variant="outlined" sx={{ ...productsPageButtonSx(visual.header.cta), pointerEvents: "none" }}>
                Ver carta
              </Button>
            </ProductsPreviewTarget>
          ) : null}
        </Stack>
      </Box>

      <Box sx={{ px: isDesktop ? 4 : 1.5, pb: 5, minWidth: 0 }}>
        {(page.showSearch !== false || compactFilters) ? (
          <Box
            sx={{
              mt: -2.5,
              p: 1.4,
              ...productsPageSurfaceSx(visual.search.shell),
              display: "flex",
              gap: 1.25,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            {page.showSearch !== false ? (
              <ProductsPreviewTarget targetId="search" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  data-preview-passive="true"
                  fullWidth
                  size="small"
                  disabled
                  placeholder={page.searchPlaceholder || "Buscar por nombre, servicio, código, categoría o subcategoría"}
                  InputProps={{ startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: visual.search.input.iconColor }} /> }}
                  sx={productsPageInputSx(visual.search.input)}
                />
              </ProductsPreviewTarget>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0 }} />
            )}

            {compactFilters ? (
              <ProductsPreviewTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ flex: "0 0 auto" }}>
                <Button
                  data-preview-passive="true"
                  tabIndex={-1}
                  variant="outlined"
                  startIcon={<FilterListRoundedIcon />}
                  sx={{ ...productsPageButtonSx(visual.filters.mobileTrigger), minHeight: 40, minWidth: isMobile ? 42 : 118, px: isMobile ? 1 : 1.5, pointerEvents: "none" }}
                >
                  {isMobile ? null : (page.filtersLabel || "Filtros")}
                </Button>
              </ProductsPreviewTarget>
            ) : null}
          </Box>
        ) : null}

        {page.showSearch === false ? (
          <Box sx={{ mt: compactFilters ? 1 : -2.5 }}>
            <ProductsHiddenTarget targetId="search" activeTarget={activeTarget} onSelect={onSelectTarget} label="Buscador" />
          </Box>
        ) : null}

        {editable ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ mt: 2.4, mb: -1, minWidth: 0 }}>
            <ProductsPreviewTarget targetId="sort" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
              <Chip data-preview-passive="true" size="small" icon={<TuneRoundedIcon />} label={`Orden · ${sortPreviewLabel(page)}`} />
            </ProductsPreviewTarget>
            <ProductsPreviewTarget targetId="pagination" activeTarget={activeTarget} onSelect={onSelectTarget}>
              <Chip data-preview-passive="true" size="small" variant="outlined" label={`${Number(page.pageSize) || 24} por página`} />
            </ProductsPreviewTarget>
          </Stack>
        ) : null}

        {!filtersVisible ? (
          <Box sx={{ mt: 3 }}>
            <ProductsHiddenTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} label="Filtros" />
          </Box>
        ) : null}

        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: filtersVisible && isDesktop ? "220px minmax(0,1fr)" : "minmax(0,1fr)",
            gap: 2.5,
            minWidth: 0,
          }}
        >
          {filtersVisible && isDesktop ? (
            <ProductsPreviewTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ alignSelf: "start", minWidth: 0 }}>
              <Box data-preview-passive="true" sx={{ ...productsPageSurfaceSx(visual.filters.desktopSurface), p: 2, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <FilterListRoundedIcon fontSize="small" sx={{ color: visual.filters.icon.color }} />
                  <Typography fontWeight={900} sx={{ color: visual.filters.title.color }}>{page.filtersLabel || "Filtros"}</Typography>
                </Stack>
                <Stack spacing={1.2}>
                  {page.showTypeFilter !== false ? <Chip label={page.typeFilterLabel || "Tipo"} variant="outlined" sx={productsPageChipSx(visual.filters.chip)} /> : null}
                  {page.showCategoryFilter !== false ? <Chip label={page.categoryFilterLabel || "Categoría"} variant="outlined" sx={productsPageChipSx(visual.filters.chip)} /> : null}
                  {page.showSubcategoryFilter !== false ? <Chip label={page.subcategoryFilterLabel || "Subcategoría"} variant="outlined" sx={productsPageChipSx(visual.filters.chip)} /> : null}
                </Stack>
              </Box>
            </ProductsPreviewTarget>
          ) : null}

          <Box sx={{ minWidth: 0 }}>
            {previewState === "loading" ? (
              <Box
                role="status"
                aria-label={page.loadingText ?? "Cargando catálogo…"}
                sx={{ minHeight: 360, display: "grid", placeItems: "center", minWidth: 0, bgcolor: visual.states.loading.backgroundColor, borderRadius: 2 }}
              >
                <Stack spacing={1.2} alignItems="center">
                  <CircularProgress sx={{ color: visual.states.loading.progressColor }} />
                  <Typography sx={{ color: visual.states.loading.textColor }}>{page.loadingText ?? "Cargando catálogo…"}</Typography>
                </Stack>
              </Box>
            ) : previewState === "empty" ? (
              <Paper
                variant="outlined"
                sx={{ ...productsPageSurfaceSx(visual.states.empty.surface), p: isMobile ? 3 : 5, textAlign: "center", minWidth: 0 }}
              >
                <Typography variant="h4" sx={{ color: visual.states.empty.title.color }}>
                  {emptyTitle}
                </Typography>
                <Typography sx={{ mt: 1, color: visual.states.empty.description.color }}>
                  {emptyText}
                </Typography>
                <Button
                  data-preview-passive="true"
                  tabIndex={-1}
                  variant="contained"
                  sx={{ ...productsPageButtonSx(visual.states.empty.resetButton), mt: 3, pointerEvents: "none" }}
                >
                  {emptyResetLabel}
                </Button>
              </Paper>
            ) : (
              <>
                {editable ? (
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.2 }}>
                    <Chip size="small" variant="outlined" label="Contenido de ejemplo" />
                    <Typography variant="caption" sx={{ color: visual.states.empty.description.color }}>
                      El diseño se aplica al catálogo real; estos productos no se guardan.
                    </Typography>
                  </Stack>
                ) : null}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${isDesktop ? 3 : 2}, minmax(0,1fr))`,
                    gap: `${cardGap}px`,
                    minWidth: 0,
                  }}
                >
                  {["Producto destacado", "Servicio principal", "Nueva colección", "Más vendido", "Producto especial", "Servicio premium"].map((name, index) => (
                    <ProductsPreviewTarget
                      key={name}
                      targetId="cards"
                      activeTarget={activeTarget}
                      onSelect={onSelectTarget}
                      label="Cards del catálogo"
                      sx={{ minWidth: 0 }}
                    >
                      <Box
                        data-preview-passive="true"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: cardImageHorizontal
                            ? (cardImageRight ? "62% minmax(0,38%)" : "38% minmax(0,62%)")
                            : "minmax(0,1fr)",
                          gridTemplateRows: cardImageHorizontal
                            ? "minmax(0,1fr)"
                            : (showCardImage ? "auto minmax(0,1fr)" : "minmax(0,1fr)"),
                          minHeight: cardImageHorizontal ? cardImageHeight : undefined,
                          minWidth: 0,
                          overflow: "hidden",
                          borderRadius: `${cardRadius}px`,
                          bgcolor: cardBackground,
                          border: cards.borderColor ? `1px solid ${cards.borderColor}` : "1px solid rgba(25,36,47,.08)",
                          boxShadow: cardShadow,
                          position: "relative",
                          isolation: "isolate",
                          "&::before": cardBackgroundImage ? {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            zIndex: 0,
                            pointerEvents: "none",
                            backgroundImage: `url("${cardBackgroundImage.replace(/"/g, "%22")}")`,
                            backgroundSize: cardBackgroundImageFit,
                            backgroundPosition: cardBackgroundImagePosition,
                            backgroundRepeat: "no-repeat",
                            opacity: cardBackgroundImageOpacity,
                          } : undefined,
                          "& > *": { position: "relative", zIndex: 1 },
                        }}
                      >
                        {showCardImage ? (
                          <Box
                            sx={{
                              minWidth: 0,
                              overflow: "hidden",
                              bgcolor: cardImageFit === "contain" ? "#fff" : "rgba(25,36,47,.08)",
                              gridColumn: cardImageHorizontal ? (cardImageRight ? 2 : 1) : 1,
                              gridRow: cardImageHorizontal ? 1 : 1,
                              minHeight: cardImageHorizontal ? cardImageHeight : 0,
                            }}
                          >
                            <Box
                              component="img"
                              src={PREVIEW_PRODUCT_IMAGE}
                              alt=""
                              aria-hidden="true"
                              sx={{
                                display: "block",
                                width: "100%",
                                height: cardImageHorizontal ? "100%" : previewImageHeight,
                                minHeight: cardImageHorizontal ? cardImageHeight : undefined,
                                objectFit: cardImageFit,
                                objectPosition: previewCardImagePosition(cardImagePosition),
                              }}
                            />
                          </Box>
                        ) : null}
                        <Box
                          sx={{
                            p: 1.5,
                            minWidth: 0,
                            gridColumn: cardImageHorizontal ? (cardImageRight ? 1 : 2) : 1,
                            gridRow: cardImageHorizontal ? 1 : (showCardImage ? 2 : 1),
                            alignSelf: "stretch",
                          }}
                        >
                          {cards.showType !== false ? (
                            <Typography variant="caption" sx={{ color: cards.typeColor || "#F35A3A", fontWeight: 850 }}>
                              Producto
                            </Typography>
                          ) : null}
                          {cards.showTitle !== false ? (
                            <Typography sx={{ mt: 0.3, fontWeight: 900, color: cards.titleColor || "#1A1A2E", overflowWrap: "anywhere" }}>
                              {name}
                            </Typography>
                          ) : null}
                          {cards.showCategory !== false ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: cards.categoryColor || "#5B5D73" }}>
                              Categoría
                            </Typography>
                          ) : null}
                          {cards.showSubcategory === true ? (
                            <Typography variant="caption" sx={{ display: "block", color: cards.subcategoryColor || "#5B5D73" }}>
                              Subcategoría
                            </Typography>
                          ) : null}
                          {cards.showDescription !== false ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.65, color: cards.descriptionColor || "#5B5D73" }}>
                              Descripción breve de ejemplo.
                            </Typography>
                          ) : null}
                          {cards.showDuration !== false ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: cards.descriptionColor || "#5B5D73" }}>
                              Duración: 60 min
                            </Typography>
                          ) : null}
                          {cards.showCapabilities !== false ? (
                            <Stack direction="row" spacing={0.45} useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
                              <Chip size="small" label="Reserva" />
                              <Chip size="small" label="Entrega" />
                            </Stack>
                          ) : null}
                          {cards.showPrice !== false ? (
                            <Typography sx={{ mt: 0.8, fontWeight: 900, color: cards.priceColor || "#19242F" }}>
                              $ {index % 2 ? "25.000" : "18.500"}
                            </Typography>
                          ) : null}
                          {cards.showQuickCart !== false ? (
                            <Chip size="small" label="Compra rápida" sx={{ mt: 0.7 }} />
                          ) : null}
                          {cards.showButton !== false ? (
                            <Button
                              tabIndex={-1}
                              size="small"
                              variant="contained"
                              fullWidth
                              sx={{
                                mt: 1.2,
                                pointerEvents: "none",
                                textTransform: "none",
                                color: cards.buttonTextColor || "#FFFFFF",
                                bgcolor: cards.buttonBackgroundColor || "#F35A3A",
                                "&:hover": {
                                  color: cards.buttonHoverTextColor || cards.buttonTextColor || "#FFFFFF",
                                  bgcolor: cards.buttonHoverBackgroundColor || cards.buttonBackgroundColor || "#F35A3A",
                                },
                              }}
                            >
                              {cards.ctaLabel || "Ver detalle"}
                            </Button>
                          ) : null}
                        </Box>
                      </Box>
                    </ProductsPreviewTarget>
                  ))}
                </Box>

                <ProductsPreviewTarget targetId="pagination" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 3, minWidth: 0 }}>
                  <Box data-preview-passive="true" sx={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
                    {page.paginationMode === "loadMore" ? (
                      <Button tabIndex={-1} variant="outlined" sx={{ ...productsPageButtonSx(visual.pagination.loadMoreButton), pointerEvents: "none" }}>
                        {page.loadMoreLabel || "Cargar más"}
                      </Button>
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" justifyContent="center">
                        <Button tabIndex={-1} size="small" variant="outlined" sx={{ ...productsPageButtonSx(visual.pagination.previousButton), pointerEvents: "none" }}>Anterior</Button>
                        <Typography variant="body2" sx={{ color: visual.pagination.counter.color }}>Página 1 de 4</Typography>
                        <Button tabIndex={-1} size="small" variant="outlined" sx={{ ...productsPageButtonSx(visual.pagination.nextButton), pointerEvents: "none" }}>Siguiente</Button>
                      </Stack>
                    )}
                  </Box>
                </ProductsPreviewTarget>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
    </ProductsPreviewEditingContext.Provider>
  );
}

export function ProductsPageDraftPreview({ form, device, gastronomicMenuEnabled = false, productsEnabled = true }) {
  const previewForm = resolveSiteExperienceDefaults(form, { gastronomicMenuEnabled, productsEnabled });
  const showHeaderCta = gastronomicMenuEnabled && !isCanonicalGastronomicCatalogNavigation(previewForm?.navigation, gastronomicMenuEnabled);
  return (
    <ProductsPagePreview
      form={previewForm}
      activeTarget={null}
      onSelectTarget={undefined}
      catalogContext="none"
      previewState="results"
      device={device}
      editable={false}
      showHeaderCta={showHeaderCta}
    />
  );
}

function PageCopyEditor({ targetId, page, set, visual, setVisual, catalogContext, onCatalogContextChange }) {
  let copyEditor = null;
  if (targetId === "eyebrow") copyEditor = <TextSetting label="Texto superior" value={page.eyebrow} onChange={(value) => set("eyebrow", value)} />;
  else if (targetId === "subtitle") copyEditor = <TextSetting label="Subtítulo" value={page.subtitle} onChange={(value) => set("subtitle", value)} multiline />;
  else if (targetId === "results") copyEditor = (
    <EditorGroup title="Cómo mostramos la cantidad" description="Preview: 1 resultado / 12 resultados">
      <TextSetting label="Singular" value={page.resultSingular} onChange={(value) => set("resultSingular", value)} />
      <TextSetting label="Plural" value={page.resultPlural} onChange={(value) => set("resultPlural", value)} />
    </EditorGroup>
  );
  else copyEditor = (
    <Stack spacing={1.5}>
      <TextSetting label="Título general" value={page.title} onChange={(value) => set("title", value)} helperText="Vacío = usar el nombre del catálogo de la navegación." />
      <EditorGroup title="Título según el tipo de catálogo" description="Se usa cuando el título general está vacío y corresponde distinguir el catálogo.">
        <TextSetting label="Productos" value={page.productsTitle} onChange={(value) => set("productsTitle", value)} />
        <TextSetting label="Servicios" value={page.servicesTitle} onChange={(value) => set("servicesTitle", value)} />
        <TextSetting label="Productos + servicio" value={page.hybridTitle} onChange={(value) => set("hybridTitle", value)} />
        <Divider />
        <SelectSetting label="Simular catálogo en preview" value={catalogContext} onChange={onCatalogContextChange} options={[{ value: "none", label: "Sin filtro" }, { value: "product", label: "Productos" }, { value: "service", label: "Servicios" }, { value: "hybrid", label: "Productos + servicio" }]} helperText="Sólo cambia esta vista previa; el tipo real depende del filtro que use el visitante." />
      </EditorGroup>
    </Stack>
  );
  return <Stack spacing={1.5}>{copyEditor}<ProductsHeaderPieceVisualEditor owner={targetId} visual={visual} setVisual={setVisual} /></Stack>;
}

function SearchEditor({ page, set, visual, setVisual }) {
  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar buscador" checked={page.showSearch} onChange={(value) => set("showSearch", value)} />
      <TextSetting label="Texto dentro del buscador" value={page.searchPlaceholder} onChange={(value) => set("searchPlaceholder", value)} />
      <ProductsSearchVisualEditor visual={visual} setVisual={setVisual} />
    </Stack>
  );
}

function FiltersEditor({ page, set, visual, setVisual }) {
  const filtersVisible = page.showFilters !== false;

  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar filtros" checked={page.showFilters} onChange={(value) => set("showFilters", value)} />
      {filtersVisible ? (
        <>
          <EditorGroup title="Qué filtros mostrar">
            <ToggleSetting label="Tipo" checked={page.showTypeFilter} onChange={(value) => set("showTypeFilter", value)} />
            <ToggleSetting label="Categoría" checked={page.showCategoryFilter} onChange={(value) => set("showCategoryFilter", value)} />
            <ToggleSetting label="Subcategoría" checked={page.showSubcategoryFilter} onChange={(value) => set("showSubcategoryFilter", value)} />
          </EditorGroup>
          <EditorGroup title="Textos de filtros">
            <TextSetting label="Título" value={page.filtersLabel} onChange={(value) => set("filtersLabel", value)} />
            <TextSetting label="Tipo" value={page.typeFilterLabel} onChange={(value) => set("typeFilterLabel", value)} />
            <TextSetting label="Categoría" value={page.categoryFilterLabel} onChange={(value) => set("categoryFilterLabel", value)} />
            <TextSetting label="Subcategoría" value={page.subcategoryFilterLabel} onChange={(value) => set("subcategoryFilterLabel", value)} />
            <Divider />
            <TextSetting label="Todos los tipos" value={page.allTypesLabel} onChange={(value) => set("allTypesLabel", value)} />
            <TextSetting label="Todas las categorías" value={page.allCategoriesLabel} onChange={(value) => set("allCategoriesLabel", value)} />
            <TextSetting label="Todas las subcategorías" value={page.allSubcategoriesLabel} onChange={(value) => set("allSubcategoriesLabel", value)} />
            <TextSetting label="Limpiar filtros" value={page.resetLabel} onChange={(value) => set("resetLabel", value)} />
            <TextSetting label="Aplicar filtros" value={page.applyFiltersLabel} onChange={(value) => set("applyFiltersLabel", value)} />
          </EditorGroup>
        </>
      ) : (
        <Paper
          variant="outlined"
          role="status"
          aria-live="polite"
          sx={{ p: 1.4, borderRadius: 2.3, bgcolor: "rgba(35,111,231,.035)" }}
        >
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.72)" }}>
            Los filtros están ocultos en la página. Tus opciones se conservan y volverán a estar disponibles al activarlos.
          </Typography>
        </Paper>
      )}
      <ProductsFiltersVisualEditor visual={visual} setVisual={setVisual} />
    </Stack>
  );
}

function SortEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.64)" }}>
        Este es el orden inicial con el que se abre el catálogo.
      </Typography>
      <SelectSetting
        label="Ordenar por"
        value={page.sortBy || "createdAt"}
        onChange={(value) => set("sortBy", value)}
        options={[
          { value: "createdAt", label: "Más recientes" },
          { value: "price", label: "Precio" },
          { value: "name", label: "Nombre" },
        ]}
      />
      <SegmentedChoiceSetting
        label="Dirección"
        value={page.sortDir || "desc"}
        onChange={(value) => set("sortDir", value)}
        options={[
          { value: "desc", label: "Descendente" },
          { value: "asc", label: "Ascendente" },
        ]}
      />
    </Stack>
  );
}

function PaginationEditor({ page, set, visual, setVisual }) {
  return (
    <Stack spacing={1.4}>
      <SegmentedChoiceSetting
        label="Comportamiento"
        value={page.paginationMode || "all"}
        onChange={(value) => set("paginationMode", value)}
        options={[
          { value: "all", label: "Páginas" },
          { value: "loadMore", label: "Cargar más" },
        ]}
      />
      <NumberSetting
        label="Resultados por página"
        value={page.pageSize}
        min={12}
        max={60}
        onChange={(value) => set("pageSize", value)}
        helperText="Elegí entre 12 y 60 resultados por página."
      />
      {page.paginationMode === "loadMore" ? (
        <TextSetting label="Botón Cargar más" value={page.loadMoreLabel} onChange={(value) => set("loadMoreLabel", value)} />
      ) : null}
      <ProductsPaginationVisualEditor visual={visual} setVisual={setVisual} />
    </Stack>
  );
}

function PageSurfaceEditor({ page, visual, set, setVisual, previewState, onPreviewStateChange }) {
  return (
    <Stack spacing={1.5}>
      <ProductsPageGeneralVisualEditor visual={visual} setVisual={setVisual} />
      <EditorGroup title="Mensajes y estados" description="Sólo aparecen cuando el catálogo carga, falla o no encuentra resultados.">
        <Box>
          <Typography variant="caption" sx={{ display: "block", mb: 0.8, fontWeight: 850, color: "rgba(25,36,47,.62)" }}>Estado del preview</Typography>
          <ToggleButtonGroup exclusive fullWidth size="small" value={previewState} onChange={(_, value) => value && onPreviewStateChange?.(value)} aria-label="Estado del preview del catálogo">
            <ToggleButton value="results">Resultados</ToggleButton><ToggleButton value="loading">Cargando</ToggleButton><ToggleButton value="empty">Vacío</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <TextSetting label="Cargando" value={page.loadingText} onChange={(value) => set("loadingText", value)} />
        <TextSetting label="Título sin resultados" value={page.emptyTitle} onChange={(value) => set("emptyTitle", value)} />
        <TextSetting label="Texto sin resultados" value={page.emptyText} onChange={(value) => set("emptyText", value)} multiline />
        <TextSetting label="Botón sin resultados" value={page.emptyResetLabel} onChange={(value) => set("emptyResetLabel", value)} />
      </EditorGroup>
    </Stack>
  );
}

function catalogCardsDescriptor() {
  const definition = getSegmentDefinition(SEGMENT_TYPES.FEATURED);
  const fields = (definition?.properties || []).filter((property) => String(property?.path || "").startsWith("catalog.cards."));
  return {
    kind: "card-system",
    label: "Cards del catálogo",
    fieldPrefix: "catalog.cards.",
    fields,
  };
}

export default function ProductsPageEditor({
  form,
  device,
  gastronomicMenuEnabled = false,
  productsEnabled = true,
  saving = false,
  onCommit,
  onUploadImage,
  onRemoveImage,
}) {
  const page = pageValue(form);
  const previewForm = resolveSiteExperienceDefaults(form, {
    gastronomicMenuEnabled,
    productsEnabled,
  });
  const showHeaderCta = gastronomicMenuEnabled && !isCanonicalGastronomicCatalogNavigation(previewForm?.navigation, gastronomicMenuEnabled);
  const [selectedTarget, setSelectedTarget] = React.useState(null);
  const [catalogContext, setCatalogContext] = React.useState("none");
  const [previewState, setPreviewState] = React.useState("results");
  const set = React.useCallback((key, value) => onCommit?.(`catalog.productsPage.${key}`, value), [onCommit]);
  const setVisual = React.useCallback((path, value) => onCommit?.(`catalog.productsPage.visual.${path}`, value), [onCommit]);
  const cardsDescriptor = React.useMemo(catalogCardsDescriptor, []);
  const selectedMeta = selectedTarget ? targetMeta(selectedTarget) : { label: "Productos", hint: "Tocá un bloque del preview para editarlo." };

  let editor = null;
  if (["eyebrow", "title", "subtitle", "results"].includes(selectedTarget)) {
    editor = (
      <PageCopyEditor
        targetId={selectedTarget}
        page={page}
        set={set}
        visual={page.visual}
        setVisual={setVisual}
        catalogContext={catalogContext}
        onCatalogContextChange={setCatalogContext}
      />
    );
  } else if (selectedTarget === "headerCta") {
    editor = <ProductsHeaderCtaVisualEditor visual={page.visual} setVisual={setVisual} />;
  } else if (selectedTarget === "search") {
    editor = <SearchEditor page={page} set={set} visual={page.visual} setVisual={setVisual} />;
  } else if (selectedTarget === "filters") {
    editor = <FiltersEditor page={page} set={set} visual={page.visual} setVisual={setVisual} />;
  } else if (selectedTarget === "sort") {
    editor = <SortEditor page={page} set={set} />;
  } else if (selectedTarget === "pagination") {
    editor = <PaginationEditor page={page} set={set} visual={page.visual} setVisual={setVisual} />;
  } else if (selectedTarget === "cards") {
    editor = (
      <CardSystemEditor
        descriptor={cardsDescriptor}
        form={form}
        disabled={saving}
        onCommit={onCommit}
        onUploadImage={onUploadImage}
        onRemoveImage={onRemoveImage}
      />
    );
  } else {
    editor = selectedTarget === "page" ? <PageSurfaceEditor page={page} visual={page.visual} set={set} setVisual={setVisual} previewState={previewState} onPreviewStateChange={setPreviewState} /> : null;
  }

  return (
    <PageEditorShell
      title={selectedTarget === "page" ? "Configuración general" : selectedMeta.label}
      description={selectedMeta.hint}
      surfaceLabel="Productos"
      device={device}
      saving={saving}
      contextual
      inspectorOpen={Boolean(selectedTarget)}
      onCloseInspector={() => setSelectedTarget(null)}
      preview={(
        <ProductsPagePreview
          form={previewForm}
          activeTarget={selectedTarget}
          onSelectTarget={setSelectedTarget}
          catalogContext={catalogContext}
          previewState={previewState}
          device={device}
          showHeaderCta={showHeaderCta}
        />
      )}
    >
      {selectedTarget && selectedTarget !== "page" ? (
        <PageEditorBackButton
          onClick={() => setSelectedTarget("page")}
          label="Configuración general"
          startIcon={<ArrowBackRoundedIcon />}
        />
      ) : selectedTarget === "page" ? (
        <PageEditorContextIntro>
          Estos ajustes afectan a la página completa. Para editar un bloque, cerrá este panel y tocá ese elemento en el preview.
        </PageEditorContextIntro>
      ) : null}
      {editor}
    </PageEditorShell>
  );
}
